/**
 * Cadrul aplicatiei: bara laterala cu harta de ecrane, bara de sus cu CONTEXTUL GLOBAL DE ZI,
 * comutatoarele de tema si densitate, declansatorul paletei de comenzi (`Ctrl+K`) si banda de
 * legenda permanenta.
 *
 * Contextul de zi e in bara de sus si nu in fiecare ecran, pentru ca „azi" e singura variabila
 * pe care intreaga bucla zilnica o are in comun: `Alt+←/→` schimba ziua lucratoare de oriunde.
 */

import { useEffect, useMemo, useState } from 'react';
import { Link, Outlet, useLocation, useNavigate } from '@tanstack/react-router';
import { ChevronLeft, ChevronRight, LogOut, Search } from 'lucide-react';
import { formatDayLabel, isoWeek, todayInBucharest } from '@evomag/core/format';
import { Legend } from '../components/Legend.tsx';
import { ErrorState } from '../components/ErrorState.tsx';
import { CommandPalette, type Comanda } from './CommandPalette.tsx';
import {
  ETICHETE_DENSITATE,
  ETICHETE_TEMA,
  INALTIMI_DENSITATE,
  useDensitate,
  useTema,
  type Densitate,
  type Tema,
} from './preferinte.ts';
import { ETICHETE_GRUP, GRUPURI_NAV, NAVIGATIE, type IntrareNav } from './navigatie.ts';
import { ziLucratoare } from '../lib/zile.ts';
import { perioadaImplicita } from '../lib/perioada.ts';
import { useEu, useReferinta } from '../hooks/useReferinta.ts';
import { useLogout } from '../hooks/useAuth.ts';
import { Login, SchimbaParolaObligatoriu } from '../features/auth/Login.tsx';
import { esteSesiuneExpirata } from '../lib/api-errors.ts';

const ISO_IN_CALE = /\/(?:zi|valutar)\/(\d{4}-\d{2}-\d{2})/;

export function Shell() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [tema, setTema] = useTema();
  const [densitate, setDensitate] = useDensitate();
  const [paletaDeschisa, setPaletaDeschisa] = useState(false);

  /**
   * Poarta aplicatiei.
   *
   * `GET /api/eu` e SINGURA sursa de adevar pentru „sunt autentificat": cookie-ul e `HttpOnly`,
   * deci JavaScriptul nu-l poate citi, si nici n-ar trebui. Asa nu poate exista dezacord intre ce
   * crede interfata si ce accepta API-ul.
   *
   * Nu exista redirect catre `/login`: ecranul se randeaza IN LOCUL aplicatiei, pe acelasi URL.
   * Deci un link adanc trimis intre colege supravietuieste autentificarii, in loc sa se piarda.
   */
  const eu = useEu();
  const logout = useLogout();

  /**
   * Nomenclatoarele se cer O SINGURA data, aici, din cel mai de sus punct al arborelui.
   *
   * `staleTime: Infinity` face ca fiecare ecran care apeleaza `useReferinta()` mai jos sa
   * citeasca din acelasi cache — deci calendarul, conturile si rolul sunt aceleasi peste tot, si
   * exista deja cand se deschide primul ecran. Se cere doar DUPA autentificare: altfel ar produce
   * un al doilea 401 la fiecare incarcare a paginii de login.
   */
  const referinta = useReferinta(undefined, { activ: eu.isSuccess });

  // Ziua de context: cea din URL daca ruta are una, altfel astazi in fusul Bucurestiului.
  const ziuaContext = ISO_IN_CALE.exec(pathname)?.[1] ?? todayInBucharest();
  const peValutar = pathname.startsWith('/valutar/');
  const { year, week } = isoWeek(ziuaContext);

  /** Sarbatorile sunt in calendar, nu intr-o formula: `Alt+→` de pe 23 decembrie sare punțile. */
  const calendar = referinta.data?.calendar;

  function mergiLaZi(iso: string): void {
    if (peValutar) void navigate({ to: '/valutar/$data', params: { data: iso } });
    else void navigate({ to: '/zi/$data', params: { data: iso } });
  }

  // Comenzile care nu sunt navigare. Traiesc aici, nu in paleta, ca sa existe o singura stare de
  // tema/densitate — doua `useTema()` ar da doua stari care scriu acelasi atribut.
  const comenziExtra = useMemo<readonly Comanda[]>(
    () => [
      {
        id: 'zi-azi',
        eticheta: 'Mergi la ziua de azi',
        grup: 'Zi',
        cuvinte: 'azi today curent',
        executa: () => mergiLaZi(todayInBucharest()),
      },
      {
        id: 'zi-inainte',
        eticheta: 'Ziua lucrătoare următoare',
        grup: 'Zi',
        cuvinte: 'urmatoare next alt dreapta',
        executa: () => mergiLaZi(ziLucratoare(ziuaContext, 1, calendar)),
      },
      {
        id: 'zi-inapoi',
        eticheta: 'Ziua lucrătoare precedentă',
        grup: 'Zi',
        cuvinte: 'precedenta anterioara prev alt stanga',
        executa: () => mergiLaZi(ziLucratoare(ziuaContext, -1, calendar)),
      },
      ...(['light', 'dark', 'sistem'] as const).map((t) => ({
        id: `tema-${t}`,
        eticheta: `Temă: ${ETICHETE_TEMA[t]}`,
        grup: 'Preferințe',
        cuvinte: `tema mod ${t} intuneric luminos`,
        executa: () => setTema(t),
      })),
      ...(['compact', 'standard', 'relaxat'] as const).map((d) => ({
        id: `densitate-${d}`,
        eticheta: `Densitate: ${ETICHETE_DENSITATE[d]}`,
        grup: 'Preferințe',
        cuvinte: `densitate rand inaltime ${d} ${INALTIMI_DENSITATE[d]}`,
        executa: () => setDensitate(d),
      })),
    ],
    // `mergiLaZi` depinde de `ziuaContext` si `peValutar`; ambele sunt in lista.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [ziuaContext, peValutar, calendar, setTema, setDensitate],
  );

  useEffect(() => {
    function laTasta(e: KeyboardEvent): void {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setPaletaDeschisa(true);
        return;
      }
      // `Alt+←/→` = ziua lucratoare precedenta/urmatoare, de oriunde din aplicatie.
      if (e.altKey && !e.shiftKey && (e.key === 'ArrowLeft' || e.key === 'ArrowRight')) {
        e.preventDefault();
        mergiLaZi(ziLucratoare(ziuaContext, e.key === 'ArrowRight' ? 1 : -1, calendar));
      }
    }
    window.addEventListener('keydown', laTasta);
    return () => window.removeEventListener('keydown', laTasta);
    // `mergiLaZi` depinde doar de cele doua valori de mai jos si de `navigate`, care e stabil.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ziuaContext, peValutar, calendar]);

  // Prima incarcare: nu stim inca daca avem sesiune. Un ecran de login aratat pentru 200 ms si
  // apoi inlocuit cu aplicatia ar fi mai rau decat o pauza scurta — dar pauza trebuie sa se VADA,
  // altfel o pagina complet alba se citeste tot ca „nu se intampla nimic".
  if (eu.isPending) {
    return (
      <div className="flex min-h-dvh flex-col items-center justify-center gap-2 bg-page text-ink-2">
        <span
          aria-hidden="true"
          className="inline-block h-6 w-6 rounded-[var(--radius-sm)]"
          style={{ background: 'var(--bank-bcr)' }}
        />
        <p className="text-12" role="status">
          Se verifică sesiunea…
        </p>
      </div>
    );
  }

  // 401/403 → nu esti (sau nu mai esti) autentificat. Orice alta eroare e o problema de retea sau
  // de server, si atunci ecranul de login ar minti: nu parola e problema.
  if (eu.isError) {
    if (esteSesiuneExpirata(eu.error)) return <Login />;
    return (
      <div className="flex min-h-dvh items-center justify-center bg-page p-4">
        <ErrorState
          titlu="Nu am putut contacta serverul"
          detaliu={eu.error.message}
          onReincearca={() => void eu.refetch()}
        />
      </div>
    );
  }

  // Parola pusa de un administrator a fost dictata sau trimisa pe un canal necontrolat de noi.
  // Pana se schimba, nu se vede nimic altceva.
  if (eu.data?.mustChangePassword === true) {
    return <SchimbaParolaObligatoriu email={eu.data.email} />;
  }

  return (
    <div className="flex min-h-dvh flex-col bg-page text-ink-1">
      <a className="skip-link fara-print" href="#continut">
        Sari la conținut
      </a>

      <div className="flex min-h-dvh">
        {/* --- bara laterala ------------------------------------------------------------------ */}
        <nav
          className="fara-print sticky top-0 flex h-dvh shrink-0 flex-col gap-3 overflow-y-auto border-r border-hairline bg-raised p-2"
          style={{ width: 'var(--shell-rail-w)' }}
          aria-label="Navigare principală"
        >
          <div className="flex items-center gap-2 px-1 py-2">
            <span
              aria-hidden="true"
              className="inline-block h-5 w-5 rounded-[var(--radius-sm)]"
              style={{ background: 'var(--bank-bcr)' }}
            />
            <span className="text-13 font-medium">Trezorerie evoMAG</span>
          </div>

          {GRUPURI_NAV.map((grup) => (
            <div key={grup} className="flex flex-col gap-0.5">
              <span className="px-2 text-11 tracking-wide text-ink-3 uppercase">
                {ETICHETE_GRUP[grup]}
              </span>
              {NAVIGATIE.filter((n) => n.grup === grup).map((n) => (
                <LinkNav key={n.cheie} intrare={n} ziuaContext={ziuaContext} />
              ))}
            </div>
          ))}
        </nav>

        {/* --- coloana de continut ------------------------------------------------------------ */}
        <div className="flex min-w-0 grow flex-col">
          <header
            className="fara-print sticky top-0 z-10 flex flex-wrap items-center gap-3 border-b border-hairline bg-raised px-3"
            style={{ minHeight: 'var(--shell-topbar-h)' }}
          >
            <div className="flex items-center gap-1">
              <button
                type="button"
                className="hit-24 chip"
                aria-label="Ziua lucrătoare precedentă (Alt+←)"
                onClick={() => mergiLaZi(ziLucratoare(ziuaContext, -1, calendar))}
              >
                <ChevronLeft size={14} aria-hidden="true" />
              </button>
              <span className="num text-13 font-medium" aria-live="polite">
                {formatDayLabel(ziuaContext)}
              </span>
              <button
                type="button"
                className="hit-24 chip"
                aria-label="Ziua lucrătoare următoare (Alt+→)"
                onClick={() => mergiLaZi(ziLucratoare(ziuaContext, 1, calendar))}
              >
                <ChevronRight size={14} aria-hidden="true" />
              </button>
              <span className="badge num" title="Săptămâna ISO, ca în etichetele „week NN” din fișier">
                {year}-W{String(week).padStart(2, '0')}
              </span>
              <Link
                to="/zi/$data"
                params={{ data: todayInBucharest() }}
                className="hit-24 chip"
                title="Revino la ziua de azi"
              >
                Azi
              </Link>
            </div>

            <button
              type="button"
              className="hit-24 chip ml-auto flex items-center gap-1"
              onClick={() => setPaletaDeschisa(true)}
            >
              <Search size={12} aria-hidden="true" />
              Caută sau execută
              <span className="num text-ink-3">Ctrl+K</span>
            </button>

            <Comutator<Densitate>
              eticheta="Densitate"
              valori={['compact', 'standard', 'relaxat']}
              curent={densitate}
              etichete={ETICHETE_DENSITATE}
              titluri={INALTIMI_DENSITATE}
              onSchimba={setDensitate}
            />

            <Comutator<Tema>
              eticheta="Temă"
              valori={['light', 'dark', 'sistem']}
              curent={tema}
              etichete={ETICHETE_TEMA}
              onSchimba={setTema}
            />

            {/* Cine e logat si cum iese. Cat timp aplicatia statea in spatele Cloudflare Access,
                niciunul dintre cele doua n-avea unde sa fie — sesiunea se incheia in alt panou. */}
            <span className="flex items-center gap-1 border-l border-hairline pl-3">
              <span className="text-11 text-ink-2" title={eu.data?.email}>
                {eu.data?.displayName ?? ''}
              </span>
              <span className="badge">{eu.data?.role}</span>
              <button
                type="button"
                className="hit-24 chip"
                title="Închide sesiunea"
                disabled={logout.isPending}
                onClick={() => logout.mutate()}
              >
                <LogOut size={12} aria-hidden="true" />
                <span className="doar-sr">Ieși din cont</span>
              </button>
            </span>
          </header>

          {/* Banda de legenda, permanenta. */}
          <div
            className="flex items-center gap-3 border-b border-hairline bg-sticky px-3 py-1"
            style={{ minHeight: 'var(--shell-legend-h)' }}
          >
            <Legend />
          </div>

          <main id="continut" className="min-w-0 grow">
            <Outlet />
          </main>
        </div>
      </div>

      <CommandPalette
        deschisa={paletaDeschisa}
        ziuaContext={ziuaContext}
        perioada={perioadaImplicita(ziuaContext)}
        comenziExtra={comenziExtra}
        onInchide={() => setPaletaDeschisa(false)}
      />
    </div>
  );
}

/**
 * Un `<Link>` per fel de destinatie, ca TypeScript sa verifice si calea si parametrii. O cale ca
 * text ar strica exact garantia pentru care am ales TanStack Router.
 */
function LinkNav({ intrare, ziuaContext }: { intrare: IntrareNav; ziuaContext: string }) {
  const clasa =
    'hit-24 flex items-center gap-2 rounded-[var(--radius-md)] px-2 py-1.5 text-13 text-ink-2 hover:bg-hovered aria-[current=page]:bg-select aria-[current=page]:font-medium aria-[current=page]:text-ink-1';
  const Icon = intrare.icon;
  const continut = (
    <>
      <Icon size={14} aria-hidden="true" className="shrink-0" />
      <span className="truncate">{intrare.eticheta}</span>
    </>
  );

  if (intrare.dest.fel === 'zi') {
    return (
      <Link to="/zi/$data" params={{ data: ziuaContext }} className={clasa} title={intrare.intrebare}>
        {continut}
      </Link>
    );
  }
  if (intrare.dest.fel === 'valutar') {
    return (
      <Link
        to="/valutar/$data"
        params={{ data: ziuaContext }}
        className={clasa}
        title={intrare.intrebare}
      >
        {continut}
      </Link>
    );
  }
  if (intrare.dest.fel === 'raport') {
    return (
      <Link
        to="/raport/$perioada"
        params={{ perioada: perioadaImplicita(ziuaContext) }}
        className={clasa}
        title={intrare.intrebare}
      >
        {continut}
      </Link>
    );
  }
  return (
    <Link to={intrare.dest.to} className={clasa} title={intrare.intrebare}>
      {continut}
    </Link>
  );
}

/** Comutator segmentat generic, cu `aria-pressed` pe fiecare optiune. */
function Comutator<T extends string>({
  eticheta,
  valori,
  curent,
  etichete,
  titluri,
  onSchimba,
}: {
  eticheta: string;
  valori: readonly T[];
  curent: T;
  etichete: Record<T, string>;
  titluri?: Record<T, string>;
  onSchimba: (v: T) => void;
}) {
  return (
    <div className="flex items-center gap-1" role="group" aria-label={eticheta}>
      <span className="text-11 text-ink-3">{eticheta}</span>
      {valori.map((v) => (
        <button
          key={v}
          type="button"
          className="hit-24 chip"
          aria-pressed={v === curent}
          title={titluri ? `${etichete[v]} · rând de ${titluri[v]}` : etichete[v]}
          style={v === curent ? { borderColor: 'var(--select-line)', color: 'var(--ink-1)' } : undefined}
          onClick={() => onSchimba(v)}
        >
          {etichete[v]}
        </button>
      ))}
    </div>
  );
}
